import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { YandexMap } from '../../components/YandexMap';
import './PlatesGamePage.css';

interface PlateEntry {
  id: string;
  plateNumber: string;
  latitude: number;
  longitude: number;
  is_valid: boolean;
  is_duplicate: boolean;
  fraud_warning?: string;
  created_at: string;
}

interface GameData {
  id: string;
  gameType: string;
  status: string;
  platesInfo: {
    digital_part: number;
    total_combinations: number;
    found_combinations: number;
  };
  entries: PlateEntry[];
}

export function PlatesGamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [plateNumber, setPlateNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);

  useEffect(() => {
    loadGame();
    getLocation();
    
    // Сохраняем текущую игру как последнюю
    if (gameId) {
      localStorage.setItem('last-game', JSON.stringify({ gameId, gameType: 'plates' }));
    }
  }, [gameId]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Не удалось получить геолокацию');
        }
      );
    } else {
      alert('Геолокация не поддерживается');
    }
  };

  const loadGame = async () => {
    try {
      const response = await api.get(`/games/${gameId}`);
      setGame(response.data);
    } catch (error) {
      console.error('Failed to load game:', error);
      alert('Ошибка загрузки игры');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!plateNumber || !location) {
      alert('Введите номер и разрешите геолокацию');
      return;
    }

    // Validate plate format (БЦЦЦББРР)
    const plateRegex = /^[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}\d{2,3}$/;
    if (!plateRegex.test(plateNumber.toUpperCase())) {
      alert('Неверный формат номера. Формат: БЦЦЦББРР (например: А123БВ77)');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/games/${gameId}/plates/submit`, {
        plateNumber: plateNumber.toUpperCase(),
        latitude: location.lat,
        longitude: location.lng,
        accuracy: location.accuracy,
      });

      if (response.data.fraudWarning) {
        alert(`Предупреждение: ${response.data.fraudWarning.join(', ')}`);
      }

      if (response.data.isValid) {
        alert('Номер успешно добавлен!');
        setPlateNumber('');
        getLocation();
      } else if (response.data.isDuplicate) {
        alert('Этот номер уже был введён');
      } else {
        alert('Номер не соответствует цифровой части');
      }

      await loadGame();
    } catch (error: any) {
      // Обрабатываем офлайн-режим
      if (error.response?.data?.offline || error.isOffline) {
        alert('Запрос сохранен. Будет отправлен при восстановлении соединения.');
        // Обновляем локально для отображения
        if (game) {
          const newEntry = {
            id: `offline-${Date.now()}`,
            plateNumber: plateNumber.toUpperCase(),
            latitude: location.lat,
            longitude: location.lng,
            is_valid: false, // Будет определено при синхронизации
            is_duplicate: false,
            created_at: new Date().toISOString(),
          };
          setGame({
            ...game,
            entries: [newEntry, ...game.entries],
          });
        }
        setPlateNumber('');
        getLocation();
      } else {
        alert(error.response?.data?.error?.message || 'Ошибка');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (!game) {
    return <div>Игра не найдена</div>;
  }

  const { platesInfo, entries } = game;

  return (
    <div className="plates-game-page">
      <div className="game-header">
        <button className="back-button" onClick={() => {
          // Устанавливаем флаг, что пользователь явно вышел на главную
          localStorage.setItem('skip-auto-redirect', 'true');
          navigate('/');
        }}>
          ← Назад
        </button>
        <h1>Номера: {String(platesInfo.digital_part).padStart(3, '0')}</h1>
      </div>

      <div className="game-stats">
        <div className="stat">
          <span className="stat-label">Найдено:</span>
          <span className="stat-value">{platesInfo.found_combinations}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Всего:</span>
          <span className="stat-value">{platesInfo.total_combinations}</span>
        </div>
      </div>

      <div className="input-section">
        <input
          type="text"
          placeholder="Введите номер (например: А123БВ77)"
          value={plateNumber}
          onChange={(e) => {
            let value = e.target.value.toUpperCase();
            // Разрешаем только русские буквы, цифры и ограничиваем длину
            value = value.replace(/[^АВЕКМНОРСТУХ0-9]/g, '');
            if (value.length <= 9) {
              setPlateNumber(value);
            } else {
              // Если длина превышает 9, обрезаем до 9 символов
              setPlateNumber(value.slice(0, 9));
            }
          }}
          onPaste={(e) => {
            // Обрабатываем вставку текста
            e.preventDefault();
            const pastedText = (e.clipboardData || (window as any).clipboardData).getData('text');
            if (pastedText) {
              let value = pastedText.toUpperCase();
              value = value.replace(/[^АВЕКМНОРСТУХ0-9]/g, '').slice(0, 9);
              setPlateNumber(value);
            }
          }}
          onInput={(e) => {
            // Дополнительная обработка для обеспечения работы ввода
            const target = e.target as HTMLInputElement;
            if (target.value !== plateNumber) {
              let value = target.value.toUpperCase();
              value = value.replace(/[^АВЕКМНОРСТУХ0-9]/g, '').slice(0, 9);
              setPlateNumber(value);
            }
          }}
          onKeyDown={(e) => {
            // Разрешаем Enter для отправки
            if (e.key === 'Enter' && plateNumber && location && !submitting) {
              handleSubmit();
            }
          }}
          className="plate-input"
          maxLength={9}
          autoComplete="off"
          autoFocus
          readOnly={false}
          disabled={false}
        />
        <button
          onClick={handleSubmit}
          disabled={submitting || !location}
          className="submit-button"
        >
          {submitting ? 'Отправка...' : 'Отправить'}
        </button>
        {!location && (
          <p className="location-warning">Ожидание геолокации...</p>
        )}
      </div>

      <div className="map-container">
        <YandexMap
          center={entries.length > 0 
            ? [entries[0].latitude, entries[0].longitude]
            : [55.7558, 37.6173]}
          zoom={10}
          markers={entries.map((entry) => ({
            id: entry.id,
            position: [entry.latitude, entry.longitude],
            content: `
              <div>
                <strong>${entry.plateNumber}</strong><br/>
                ${entry.is_valid ? '✓ Валидный' : '✗ Невалидный'}
                ${entry.is_duplicate ? '<br/>Дубликат' : ''}
                ${entry.fraud_warning ? `<br/><span style="color: orange;">⚠ ${entry.fraud_warning}</span>` : ''}
              </div>
            `,
            color: entry.is_valid ? 'green' : 'red',
          }))}
        />
      </div>

      <div className="entries-list">
        <h3>История вводов</h3>
        {entries.length === 0 ? (
          <p>Нет вводов</p>
        ) : (
          <div className="entries">
            {entries.slice(0, 10).map((entry) => (
              <div key={entry.id} className="entry">
                <span className={`plate-number ${entry.is_valid ? 'valid' : 'invalid'}`}>
                  {entry.plateNumber}
                </span>
                <span className="entry-time">
                  {new Date(entry.created_at).toLocaleString('ru-RU')}
                </span>
                {entry.fraud_warning && (
                  <span className="fraud-warning">⚠</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

